// Iter · Feb 2026 — Edit Package Template dialog.
// A focused editor for the fields users most commonly want to tweak
// after a template was first created: name, naming pattern, sections
// (grouping), and lines (name / category / frequency / count / section).
// For deeper changes (skill breakdown, cost model), users still create
// a package from the template and edit there.
//
// Backend: PATCH /api/v1/package-templates/{tid}

import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Plus, Trash2, Loader2, ChevronUp, ChevronDown, GripVertical } from "lucide-react";

const FREQ_OPTS = ["monthly", "weekly", "daily", "once", "quarterly"];

export default function EditTemplateDialog({ template, wsId, onClose, onSaved }) {
  const [name, setName] = useState(template?.name || "");
  const [namingPattern, setNamingPattern] = useState(template?.naming_pattern || "");
  const [notesHtml, setNotesHtml] = useState(template?.notes_html || "");
  const [sections, setSections] = useState(() => (template?.sections || []).map((s) => ({ ...s })));
  const [lines, setLines] = useState(() => (template?.lines || []).map((l) => ({ ...l })));
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  // Iter · Feb 2026 — Line drag reorder. We keep state for the visual
  // "dragging / hovered" cues AND stash the source line id in
  // dataTransfer so the drop handler works even in synthetic-event
  // test environments (Playwright HTML5 DnD limitation).
  const [dragLineId, setDragLineId] = useState(null);
  const [dragOverLineId, setDragOverLineId] = useState(null);

  const readDragId = (e) =>
    dragLineId
    || e.dataTransfer?.getData("application/x-line-id")
    || e.dataTransfer?.getData("text/plain")
    || null;

  const onLineDragStart = (id, e) => {
    setDragLineId(id);
    if (e?.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("application/x-line-id", id);
      e.dataTransfer.setData("text/plain", id);
    }
  };
  const onLineDragEnd = () => {
    setDragLineId(null);
    setDragOverLineId(null);
  };
  const doReorder = (movingId, targetId, targetSectionId) => {
    if (!movingId || movingId === targetId) return;
    setLines((prev) => {
      const src = prev.find((l) => l.id === movingId);
      if (!src) return prev;
      const rest = prev.filter((l) => l.id !== movingId);
      const targetIdx = rest.findIndex((l) => l.id === targetId);
      const movedSrc = { ...src, section_id: targetSectionId ?? src.section_id };
      if (targetIdx < 0) return [...rest, movedSrc];
      return [...rest.slice(0, targetIdx), movedSrc, ...rest.slice(targetIdx)];
    });
  };
  const onLineDrop = (e, targetId, targetSectionId) => {
    const id = readDragId(e);
    doReorder(id, targetId, targetSectionId);
  };
  const onLineDropAtEndOfSection = (e, targetSectionId) => {
    const id = readDragId(e);
    if (!id) return;
    setLines((prev) => {
      const src = prev.find((l) => l.id === id);
      if (!src) return prev;
      const rest = prev.filter((l) => l.id !== id);
      const movedSrc = { ...src, section_id: targetSectionId ?? null };
      return [...rest, movedSrc];
    });
  };

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/content-categories`)
      .then((r) => setCategories(r.data || []))
      .catch(() => {});
  }, [wsId]);

  // Group lines by section for display; keep an "Ungrouped" bucket at end.
  const grouped = useMemo(() => {
    const bySection = new Map();
    bySection.set(null, []);
    for (const s of sections) bySection.set(s.id, []);
    for (const ln of lines) {
      const sid = ln.section_id && bySection.has(ln.section_id) ? ln.section_id : null;
      bySection.get(sid).push(ln);
    }
    return bySection;
  }, [lines, sections]);

  const addLine = (section_id = null) => {
    setLines((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        name: "New deliverable",
        category: "other",
        frequency: "monthly",
        count: 1,
        section_id,
        activity_type_id: null,
        effort_hours: 0,
        skill_breakdown: [],
      },
    ]);
  };
  const updLine = (id, patch) => setLines((prev) => prev.map((l) => l.id === id ? { ...l, ...patch } : l));
  const rmLine = (id) => setLines((prev) => prev.filter((l) => l.id !== id));

  const addSection = () => {
    setSections((prev) => [
      ...prev,
      { id: `new-sec-${Date.now()}`, name: "New section", order: prev.length + 1, color: "#8b5cf6" },
    ]);
  };
  const updSection = (id, patch) => setSections((prev) => prev.map((s) => s.id === id ? { ...s, ...patch } : s));
  const rmSection = (id) => {
    setSections((prev) => prev.filter((s) => s.id !== id));
    setLines((prev) => prev.map((l) => l.section_id === id ? { ...l, section_id: null } : l));
  };
  const moveSection = (id, dir) => {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.id === id);
      const j = idx + dir;
      if (idx < 0 || j < 0 || j >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr.map((s, i) => ({ ...s, order: i + 1 }));
    });
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const r = await api.patch(`/package-templates/${template.id}`, {
        name: name.trim(),
        naming_pattern: namingPattern || null,
        notes_html: notesHtml || null,
        sections: sections.map((s) => ({
          id: String(s.id).startsWith("new-sec-") ? null : s.id,
          name: s.name,
          order: s.order,
          color: s.color,
        })),
        lines: lines.map((l) => ({
          id: String(l.id).startsWith("new-") ? null : l.id,
          name: l.name,
          category: l.category || "other",
          frequency: l.frequency || "monthly",
          count: Number(l.count) || 1,
          section_id: l.section_id,
          activity_type_id: l.activity_type_id,
          effort_hours: Number(l.effort_hours) || 0,
          skill_breakdown: l.skill_breakdown || [],
        })),
      });
      // Iter · Feb 2026 — Surface the cascade report so users see the
      // downstream update happened.
      const report = r?.data?._cascade_report;
      if (report && (report.packages || report.content_items)) {
        toast.success(`Template saved · auto-updated ${report.packages} package${report.packages === 1 ? "" : "s"}${report.content_items ? ` · ${report.content_items} deliverables` : ""}`);
      } else {
        toast.success("Template saved");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSaving(false); }
  };

  // Iter · Feb 2026 — Live cascade preview. Whenever the lines /
  // sections change we call the backend preview endpoint (debounced
  // via a stringified fingerprint) so the Save button can show a
  // "N deliverables will be re-tagged" chip.
  const [preview, setPreview] = React.useState(null);
  React.useEffect(() => {
    if (!template?.id) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.post(`/package-templates/${template.id}/preview-category-cascade`, {
          sections: sections.map((s) => ({ id: String(s.id).startsWith("new-sec-") ? null : s.id, name: s.name })),
          lines: lines.map((l) => ({
            id: String(l.id).startsWith("new-") ? null : l.id,
            name: l.name,
            section_id: l.section_id,
            category: l.category || "other",
          })),
        });
        setPreview(r.data);
      } catch { setPreview(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [template?.id, lines, sections]);

  const catOptions = categories.length ? categories : [{ name: "other" }];

  const renderLineRow = (ln) => (
    <div key={ln.id}
         draggable
         onDragStart={(e) => onLineDragStart(ln.id, e)}
         onDragEnd={onLineDragEnd}
         onDragOver={(e) => { if (dragLineId && dragLineId !== ln.id) { e.preventDefault(); setDragOverLineId(ln.id); } }}
         onDragLeave={() => { if (dragOverLineId === ln.id) setDragOverLineId(null); }}
         onDrop={(e) => { e.preventDefault(); onLineDrop(e, ln.id, ln.section_id); setDragOverLineId(null); }}
         className={`grid grid-cols-[16px,1fr,120px,110px,80px,32px] gap-2 items-center px-2 py-1 border-b border-border/40 ${dragLineId === ln.id ? "opacity-40" : ""} ${dragOverLineId === ln.id ? "outline outline-1 outline-primary bg-primary/5" : ""}`}
         data-testid={`edit-tpl-line-${ln.id}`}>
      <span className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground" title="Drag to reorder">
        <GripVertical className="w-3.5 h-3.5" />
      </span>
      <Input
        value={ln.name}
        onChange={(e) => updLine(ln.id, { name: e.target.value })}
        className="h-8 rounded-none text-[12px]"
        placeholder="Deliverable name"
      />
      <Select value={ln.category || "other"} onValueChange={(v) => updLine(ln.id, { category: v })}>
        <SelectTrigger className="h-8 rounded-none text-[11px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          {catOptions.map((c) => (
            <SelectItem key={c.name} value={c.name}>
              <span className="inline-flex items-center gap-1.5">
                {c.color && <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />}
                {c.name}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={ln.frequency || "monthly"} onValueChange={(v) => updLine(ln.id, { frequency: v })}>
        <SelectTrigger className="h-8 rounded-none text-[11px]"><SelectValue /></SelectTrigger>
        <SelectContent>{FREQ_OPTS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
      </Select>
      <Input
        type="number" min={1}
        value={ln.count || 1}
        onChange={(e) => updLine(ln.id, { count: Number(e.target.value) || 1 })}
        className="h-8 rounded-none text-[12px] text-center"
      />
      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground hover:text-rose-600"
              onClick={() => rmLine(ln.id)}
              data-testid={`edit-tpl-line-remove-${ln.id}`}>
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  );

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl rounded-none max-h-[90vh] overflow-auto" data-testid="edit-template-dialog">
        <DialogHeader>
          <DialogTitle>Edit template — {template?.name}</DialogTitle>
          <DialogDescription className="text-[11px]">
            Rename, tweak the naming pattern, and add/rearrange sections + deliverables. Changes save via PATCH — inherited packages stay untouched unless you use Propagate.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                    className="h-9 rounded-none" data-testid="edit-tpl-name" />
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Naming pattern</Label>
            <Input value={namingPattern} onChange={(e) => setNamingPattern(e.target.value)}
                    placeholder="{seq}/{total} · {name}"
                    className="h-9 rounded-none font-mono text-[11px]"
                    data-testid="edit-tpl-naming" />
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="label-eyebrow">Sections (grouping)</Label>
            <Button size="sm" variant="outline" className="rounded-none h-7" onClick={addSection}
                    data-testid="edit-tpl-add-section">
              <Plus className="w-3 h-3 mr-1" /> Section
            </Button>
          </div>
          {sections.length === 0 && (
            <div className="text-[11px] text-muted-foreground border border-dashed border-border p-3 text-center">
              No sections yet — lines will render in a single ungrouped list.
            </div>
          )}
          <div className="space-y-1" data-testid="edit-tpl-sections">
            {sections.map((s, i) => (
              <div key={s.id} className="grid grid-cols-[24px,1fr,32px,32px,32px] gap-2 items-center px-2 py-1 border border-border/60"
                   data-testid={`edit-tpl-section-${s.id}`}>
                <span className="w-3 h-3 rounded-full" style={{ background: s.color || "#8b5cf6" }} />
                <Input value={s.name} onChange={(e) => updSection(s.id, { name: e.target.value })}
                        className="h-7 rounded-none text-[12px]" />
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => moveSection(s.id, -1)} disabled={i === 0}>
                  <ChevronUp className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => moveSection(s.id, +1)} disabled={i === sections.length - 1}>
                  <ChevronDown className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-rose-600" onClick={() => rmSection(s.id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Lines grouped by section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="label-eyebrow">Deliverables ({lines.length})</Label>
            <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => addLine(null)}
                    data-testid="edit-tpl-add-line">
              <Plus className="w-3 h-3 mr-1" /> Deliverable
            </Button>
          </div>
          <div className="grid grid-cols-[16px,1fr,120px,110px,80px,32px] gap-2 px-2 py-1 bg-muted/40 text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">
            <div></div><div>Name</div><div>Category</div><div>Frequency</div><div>Count</div><div></div>
          </div>
          {sections.map((s) => {
            const rows = grouped.get(s.id) || [];
            return (
              <div key={s.id} className="border border-border" data-testid={`edit-tpl-section-block-${s.id}`}>
                <div className="flex items-center gap-2 px-2 py-1 bg-muted/20 text-[11px] font-semibold">
                  <span className="w-2 h-2 rounded-full" style={{ background: s.color || "#8b5cf6" }} />
                  <span>{s.name}</span>
                  <span className="text-[9px] text-muted-foreground ml-1">{rows.length}</span>
                  <Button size="sm" variant="ghost" className="h-6 rounded-none text-[10px] ml-auto" onClick={() => addLine(s.id)}>
                    <Plus className="w-3 h-3 mr-1" /> Line
                  </Button>
                </div>
                {rows.map(renderLineRow)}
                {/* Drop-target zone at the end of each section (also handles empty sections). */}
                <div
                  onDragOver={(e) => { if (dragLineId) e.preventDefault(); }}
                  onDrop={(e) => { e.preventDefault(); onLineDropAtEndOfSection(e, s.id); }}
                  className={`h-2 ${dragLineId ? "bg-primary/10" : ""}`}
                  data-testid={`edit-tpl-section-drop-${s.id}`}
                />
              </div>
            );
          })}
          <div className="border border-border">
            <div className="flex items-center gap-2 px-2 py-1 bg-muted/20 text-[11px] font-semibold">
              <span className="text-muted-foreground">Ungrouped</span>
              <span className="text-[9px] text-muted-foreground">{(grouped.get(null) || []).length}</span>
              <Button size="sm" variant="ghost" className="h-6 rounded-none text-[10px] ml-auto" onClick={() => addLine(null)}>
                <Plus className="w-3 h-3 mr-1" /> Line
              </Button>
            </div>
            {(grouped.get(null) || []).map(renderLineRow)}
            <div
              onDragOver={(e) => { if (dragLineId) e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); onLineDropAtEndOfSection(e, null); }}
              className={`h-2 ${dragLineId ? "bg-primary/10" : ""}`}
              data-testid="edit-tpl-section-drop-ungrouped"
            />
          </div>
        </div>

        <DialogFooter>
          {preview && preview.template_lines_changed > 0 && (
            <div className="mr-auto flex items-center gap-2 text-[11px] text-primary border border-primary/30 bg-primary/5 px-2 py-1" data-testid="edit-tpl-cascade-preview">
              <span>
                <b>{preview.template_lines_changed}</b> line{preview.template_lines_changed === 1 ? "" : "s"} recategorized →
                will auto-update <b>{preview.packages}</b> package{preview.packages === 1 ? "" : "s"}
                {preview.content_items > 0 && <> · <b>{preview.content_items}</b> deliverable{preview.content_items === 1 ? "" : "s"}</>}
              </span>
              {preview.per_package && preview.per_package.length > 0 && (
                <HoverCard openDelay={100}>
                  <HoverCardTrigger asChild>
                    <button className="text-[10px] underline decoration-dotted underline-offset-2" data-testid="edit-tpl-cascade-preview-details">Details</button>
                  </HoverCardTrigger>
                  <HoverCardContent side="top" align="start" className="w-80 p-0 rounded-none max-h-64 overflow-auto">
                    <div className="px-3 py-2 border-b border-border bg-muted/40 text-[10px] uppercase tracking-widest font-semibold">Cascade preview</div>
                    <ul className="divide-y divide-border">
                      {preview.per_package.map((pp) => (
                        <li key={pp.package_id} className="px-3 py-1.5 text-[11px]">
                          <div className="font-medium truncate">{pp.package_name}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {pp.lines} line{pp.lines === 1 ? "" : "s"} · {pp.content_items} deliverable{pp.content_items === 1 ? "" : "s"}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </HoverCardContent>
                </HoverCard>
              )}
            </div>
          )}
          <Button variant="ghost" className="rounded-none" onClick={onClose} data-testid="edit-tpl-cancel">Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={saving}
                  data-testid="edit-tpl-save">
            {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Saving…</> : "Save template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
